import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SetGroupDTO } from 'dto/setGroup';
import Picture from 'components/Picture';
import ListItemAdd from 'components/ListItemAdd';
import InfoBox from 'components/InfoBox';
import { useStores } from 'util/mobx/stores';
import Popover from 'components/Popover';
import ArrowDotsVerticalIcon from 'components/animationIcons/ArrowDotsVerticalIcon';
import { DragDropContext, Droppable, DropResult, Draggable } from 'react-beautiful-dnd';
import { reorderGroupMaterials } from 'api/groupMaterial';
import { Can, subjectArea, actions } from 'casl/setupCaslAbility';
import { TagDTO } from 'dto/tag';
import { CheckStatus } from 'dto/materialSet';
import { observer } from 'mobx-react';
import { getItemStyle } from 'util/dragAndDrop';
import MaterialValueTags from 'components/MaterialValueTags';
import GetCameraImageButton from 'components/GetCameraImageButton';
import { AnyMaterial, SelectableType } from 'stores/searchStore';
import { subject } from '@casl/ability';
import { IsBrowser } from 'util/browser';
import ListItemDropdownAsFunction from '../ListItemDropdownAsFunction';
import EditableGroupMaterialListItem from '../EditableGroupMaterialListItem';
import SetGroupListItemMenu from './SetGroupListItemMenu';
import Tags from '../components/Tags';
import CheckListItemLottie from '../EditableGroupMaterialListItem/CheckListItemLottie';

interface Props {
  setGroup: SetGroupDTO;
  index: number;
  groupType: string;
}

const EditableSetGroupListItem = observer(({ setGroup, index, groupType }: Props) => {
  const [groupMaterials, setGroupMaterials] = useState(setGroup.groupMaterials);
  const { t: materialSetTranslation } = useTranslation(groupType);
  const { t: searchTranslation } = useTranslation('materialSearch');
  const { searchStore, materialSetsStore, domainStore } = useStores();

  const onGroupMaterialAdd = async (m: AnyMaterial) => {
    const groupMaterial = await materialSetsStore.addMaterialToMaterialSet(m, setGroup.setGroupId);
    if (groupMaterial) {
      setGroupMaterials([...groupMaterials, groupMaterial]);
    }
  };

  const onGroupMaterialDelete = async (groupMaterialId: string) => {
    setGroupMaterials([...groupMaterials.filter(gm => gm.groupMaterialId !== groupMaterialId)]);
  };

  const handleCreateGroupMaterials = () => {
    searchStore.openSearchPanel(m => onGroupMaterialAdd(m), searchTranslation('searchItem.addItem'), SelectableType.Material);
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination || !groupMaterials) return;
    const groupMaterialToBeRemoved = groupMaterials.find(groupMaterial => groupMaterial.groupMaterialId === result.draggableId);
    const oldGroupMaterials = groupMaterials.filter(groupMaterial => groupMaterial.groupMaterialId !== result.draggableId);
    if (groupMaterialToBeRemoved) {
      oldGroupMaterials.splice(result.destination.index, 0, groupMaterialToBeRemoved);
      setGroupMaterials([...oldGroupMaterials]);
      reorderGroupMaterials({
        groupMaterialIds: oldGroupMaterials.map(groupMaterial => groupMaterial.groupMaterialId)
      });
    }
  };

  useEffect(() => {
    setGroupMaterials(setGroup.groupMaterials);
  }, [setGroup.groupMaterials]);

  const renderList = (isListExpanded: null | boolean) => (
    <div className={`content_holder no_padding padding_left instruments-list ${isListExpanded ? '' : 'display-none'}`}>
      {!materialSetsStore.isCheckListVisible && (
        <Can I={actions.add} this={subject(subjectArea.groupMaterial, { departmentId: domainStore.currentDepartment.id })}>
          <ListItemAdd className="list_item_material_add in_content" onClick={handleCreateGroupMaterials}>
            <InfoBox label={materialSetTranslation('groupsList.button.createGroupMaterials')} />
          </ListItemAdd>
        </Can>
      )}
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId={setGroup.setGroupId} type="subItem">
          {provided => (
            <div ref={provided.innerRef}>
              {groupMaterials.map((groupMaterial, materialIndex) => {
                return (
                  <Draggable
                    draggableId={groupMaterial.groupMaterialId}
                    index={materialIndex}
                    key={groupMaterial.groupMaterialId}
                    isDragDisabled={!!materialSetsStore.isCheckListVisible}
                  >
                    {(draggableProvided, snapshot) => {
                      const style = getItemStyle(snapshot.isDragging, draggableProvided.draggableProps.style);

                      if (IsBrowser.chrome() && snapshot.isDragging) {
                        // Hacky Hack https://github.com/atlassian/react-beautiful-dnd/issues/1881#issuecomment-695772970
                        style.left = (draggableProvided.draggableProps.style as any)?.offsetLeft;
                        style.top = (draggableProvided.draggableProps.style as any)?.offsetTop;
                      }

                      return (
                        <div
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          {...draggableProvided.dragHandleProps}
                          style={style}
                        >
                          <EditableGroupMaterialListItem
                            groupMaterial={groupMaterial}
                            key={`${groupMaterial.groupMaterialId}`}
                            onGroupMaterialDelete={onGroupMaterialDelete}
                          />
                        </div>
                      );
                    }}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );

  const onChange = () => {
    materialSetsStore.setIsSetGroupFormFlyoutOpen(true, setGroup);
  };

  const onDelete = () => materialSetsStore.deleteSetGroup(setGroup.setGroupId);

  const onTagCreate = async (tagName: string) => {
    return materialSetsStore.createSetGroupTag(setGroup.setGroupId, tagName);
  };

  const onTagDelete = (tagId: string) => {
    return materialSetsStore.deleteTag(tagId);
  };

  const onTagUpdate = (tag: TagDTO) => {
    return materialSetsStore.updateTag(tag);
  };

  const onAddToCheckList = (status: CheckStatus) => {
    materialSetsStore.addSetGroupCheckItem(status, setGroup.setGroupId);
  };

  const onUpdateFile = async (fileId: string) => {
    await materialSetsStore.updateSetGroup({ setGroupId: setGroup.setGroupId, pictureFileId: fileId });
  };

  return (
    <ListItemDropdownAsFunction
      classPrefix="package"
      list={renderList}
      isExpandedByDefault={materialSetsStore.isCheckListVisible}
      key={setGroup.setGroupId + materialSetsStore.isCheckListVisible}
    >
      {({ setIsListExpanded, isListExpanded }) => (
        <Draggable
          index={index}
          draggableId={setGroup.setGroupId}
          key={setGroup.setGroupId}
          isDragDisabled={!!materialSetsStore.isCheckListVisible}
        >
          {(draggableProvided, snapshot) => (
            <div
              className="list_item_material"
              ref={draggableProvided.innerRef}
              {...draggableProvided.draggableProps}
              {...draggableProvided.dragHandleProps}
              style={getItemStyle(snapshot.isDragging, draggableProvided.draggableProps.style)}
            >
              <div className="image_wrapper_50">
                <div className="image_border image-border" onClick={() => setIsListExpanded(!isListExpanded)} />
                {setGroup.picture ? (
                  <Picture
                    src={setGroup.picture}
                    width={50}
                    alt=""
                    className="image_circle_50"
                    onClick={() => setIsListExpanded(!isListExpanded)}
                  />
                ) : (
                  <GetCameraImageButton buttonStyle="icon" onUpdateFile={onUpdateFile} description="" picturesOnly isAddable />
                )}

                <CheckListItemLottie
                  setGroupId={setGroup.setGroupId}
                  onStatusChanged={onAddToCheckList}
                  notEditable={!!materialSetsStore.selectedSetCheck}
                />
              </div>
              <div className="material_info w-inline-block package-info" onClick={() => setIsListExpanded(!isListExpanded)}>
                <div className="material_text material-text">
                  <div className="link-block">{setGroup.name}</div>
                  <Tags
                    tags={setGroup.tags}
                    editable={!materialSetsStore.isCheckListVisible}
                    onTagCreate={onTagCreate}
                    onTagUpdate={onTagUpdate}
                    onTagDelete={onTagDelete}
                  />
                </div>
                <MaterialValueTags setGroup={setGroup} fullInfo />
                {!materialSetsStore.isCheckListVisible && (
                  <Can I={actions.see} this={subject(subjectArea.setGroupMenu, { departmentId: domainStore.currentDepartment.id })}>
                    <Popover trigger={<ArrowDotsVerticalIcon dotsOnly />}>
                      {({ handleClose, isOpen }) => (
                        <SetGroupListItemMenu handleClose={handleClose} isOpen={!!isOpen} handleChange={onChange} handleDelete={onDelete} />
                      )}
                    </Popover>
                  </Can>
                )}
              </div>
            </div>
          )}
        </Draggable>
      )}
    </ListItemDropdownAsFunction>
  );
});

export default EditableSetGroupListItem;
